/**
 * CartePermisAM.jsx — Visuel de cérémonie "Carte Permis AM"
 * Format européen (ratio 1.586), rendu inspiré du vrai permis de conduire européen
 * avec améliorations visuelles : dégradé holographique, guillochage, hologramme AM,
 * signature imprimée, MRZ décorative, confettis + animation d'apparition.
 *
 * ⚠ Cette carte est un support de cérémonie, PAS un document officiel.
 * L'attestation officielle est délivrée par l'auto-école.
 *
 * Usage :
 *   <CartePermisAM nom="DUPONT" prenom="Léa" naissance="12/04/2011" confettis showTitle />
 */
import React, { useMemo } from 'react';
import { Trophy } from 'lucide-react';

/* Génère un numéro à 12 chiffres stable pour un couple prenom+nom */
function useNumero(prenom, nom) {
  return useMemo(() => {
    const seed = (prenom || '') + '|' + (nom || '');
    let h = 5381;
    for (let i = 0; i < seed.length; i++) h = ((h << 5) + h) ^ seed.charCodeAt(i);
    const n = Math.abs(h).toString().padStart(12, '0').slice(0, 12);
    // Format 12A 12345 6789 (français)
    return `${n.slice(0, 2)}FR${n.slice(2, 7)} ${n.slice(7, 12)}`;
  }, [prenom, nom]);
}

export function CartePermisAM({
  nom = '',
  prenom = '',
  naissance = '',
  confettis = true,
  showTitle = true,
  showMention = true,
  compact = false,
}) {
  const dateNow = new Date();
  const dd = String(dateNow.getDate()).padStart(2, '0');
  const mm = String(dateNow.getMonth() + 1).padStart(2, '0');
  const yy = dateNow.getFullYear();
  // Fin de validité : + 15 ans
  const finValid = `${dd}/${mm}/${yy + 15}`;
  const numero = useNumero(prenom, nom);

  return (
    <div
      className={`relative flex flex-col items-center ${compact ? 'gap-3' : 'gap-5'} w-full`}
      data-testid="carte-permis-container"
    >
      {/* Confettis */}
      {confettis && (
        <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1.5 h-2.5 opacity-90"
              style={{
                left: `${(i * 11) % 100}%`,
                top: `${-5 + ((i * 17) % 110)}%`,
                background: ['#ef4444', '#f59e0b', '#facc15', '#10b981', '#3b82f6', '#ec4899'][i % 6],
                transform: `rotate(${(i * 31) % 360}deg)`,
                animation: `carte-fall ${2 + (i % 4) * 0.5}s ${(i * 0.1) % 3}s linear infinite`,
              }}
            />
          ))}
        </div>
      )}

      {/* Titre cérémonie */}
      {showTitle && (
        <div className="relative z-10 text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] sm:text-xs font-black uppercase tracking-[0.24em] animate-pulse">
            <Trophy className="w-3 h-3" /> Formation validée
          </div>
          <h1
            className={`${compact ? 'text-2xl sm:text-4xl' : 'text-3xl sm:text-5xl md:text-6xl'} font-black leading-tight text-white`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            <span className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 bg-clip-text text-transparent">
              Permis AM
            </span>
            <br />
            <span className={`${compact ? 'text-lg sm:text-2xl' : 'text-xl sm:text-3xl md:text-4xl'} text-white/90`}>
              Épreuve validée
            </span>
          </h1>
        </div>
      )}

      {/* Carte permis européen */}
      <div
        className={`relative z-10 w-full ${compact ? 'max-w-xs sm:max-w-sm' : 'max-w-md sm:max-w-lg'} aspect-[1.586] rounded-2xl shadow-2xl overflow-hidden`}
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 30%, #2563eb 65%, #1e3a8a 100%)',
          boxShadow: '0 25px 60px -20px rgba(37,99,235,0.6), 0 0 0 1px rgba(255,255,255,0.08) inset, 0 -30px 60px -30px rgba(250,204,21,0.15) inset',
          animation: 'carte-appear 700ms cubic-bezier(0.34,1.56,0.64,1)',
        }}
        data-testid="carte-permis-am"
      >
        {/* Guillochage subtil (motif de sécurité) */}
        <div
          className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 6px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 6px)',
          }}
        />
        {/* Hologramme rond central (effet arc-en-ciel) */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full opacity-15 pointer-events-none"
          style={{
            background:
              'conic-gradient(from 45deg, #ff6b9d, #feca57, #48dbfb, #a29bfe, #ff6b9d)',
            filter: 'blur(2px)',
          }}
        />

        {/* Bande drapeau EU en haut */}
        <div className="absolute top-0 left-0 right-0 h-[18px] bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 flex items-center justify-between px-3 border-b border-yellow-400/50 z-10">
          <div className="flex items-center gap-1">
            {[...Array(8)].map((_, i) => (
              <span key={i} className="text-yellow-400 text-[7px] leading-none">★</span>
            ))}
          </div>
          <span className="text-[8px] font-black text-white/85 tracking-[0.18em]">
            RÉPUBLIQUE FRANÇAISE
          </span>
        </div>

        {/* Pastille EU · AM côté gauche */}
        <div className="absolute top-6 left-2 z-10">
          <div className="w-11 h-11 rounded-full bg-blue-950 border-2 border-yellow-400/70 flex items-center justify-center relative">
            <div className="absolute inset-0 rounded-full border border-yellow-400/30" style={{ transform: 'scale(0.75)' }} />
            <span className="text-yellow-400 text-[8px] font-black tracking-widest">F · AM</span>
          </div>
        </div>

        {/* Titre officiel */}
        <div className="absolute top-6 left-16 right-2 text-white z-10">
          <div className="text-[7px] font-black uppercase tracking-[0.22em] text-yellow-300/90">
            Permis de conduire · Cérémonie
          </div>
          <div
            className="text-sm sm:text-base font-black uppercase tracking-wide leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Catégorie AM
          </div>
          <div className="text-[8px] italic text-white/75 leading-tight">
            Cyclomoteurs 50 cm³ · quadricycles légers
          </div>
        </div>

        {/* Corps carte */}
        <div className="absolute bottom-2 left-2 right-2 top-[70px] flex gap-2.5 text-white z-10">
          {/* Photo stylisée */}
          <div
            className="flex-shrink-0 w-16 sm:w-[70px] aspect-[3/4] rounded-md border border-yellow-400/50 flex items-center justify-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #334155 100%)',
              boxShadow: 'inset 0 0 12px rgba(0,0,0,0.4)',
            }}
          >
            <div
              className="text-4xl sm:text-5xl font-black text-yellow-300"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 8px rgba(250,204,21,0.4)' }}
            >
              {(prenom?.[0] || 'X').toUpperCase()}
              {(nom?.[0] || '').toUpperCase()}
            </div>
            {/* Coin holographique */}
            <div
              className="absolute bottom-0 right-0 w-4 h-4 opacity-40"
              style={{ background: 'conic-gradient(from 0deg, #ff6b9d, #feca57, #48dbfb, #ff6b9d)' }}
            />
          </div>

          {/* Infos */}
          <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-2 gap-y-[3px] text-[9px] sm:text-[10px]">
            <div className="col-span-2">
              <div className="opacity-60 uppercase text-[7px] tracking-widest">1. Nom</div>
              <div className="font-black uppercase truncate text-sm leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {nom || '—'}
              </div>
            </div>
            <div className="col-span-2">
              <div className="opacity-60 uppercase text-[7px] tracking-widest">2. Prénom</div>
              <div className="font-black truncate leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {prenom || '—'}
              </div>
            </div>
            {naissance && (
              <div className="col-span-2">
                <div className="opacity-60 uppercase text-[7px] tracking-widest">3. Né(e) le</div>
                <div className="font-black tabular-nums text-[10px]">{naissance}</div>
              </div>
            )}
            <div>
              <div className="opacity-60 uppercase text-[7px] tracking-widest">4a. Délivré</div>
              <div className="font-black tabular-nums">{dd}/{mm}/{yy}</div>
            </div>
            <div>
              <div className="opacity-60 uppercase text-[7px] tracking-widest">4b. Fin</div>
              <div className="font-black tabular-nums text-white/60">{finValid}</div>
            </div>
            <div className="col-span-2">
              <div className="opacity-60 uppercase text-[7px] tracking-widest">5. N°</div>
              <div className="font-black tabular-nums text-[10px] tracking-wider">{numero}</div>
            </div>
            <div className="col-span-2">
              <div className="opacity-60 uppercase text-[7px] tracking-widest">9. Catégories</div>
              <div className="flex items-center gap-2 leading-none mt-0.5">
                <span
                  className="font-black text-xl tracking-widest text-yellow-300"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}
                >
                  AM
                </span>
                <span className="text-[8px] font-black text-white/60 tracking-widest">
                  {dd}/{mm}/{yy}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* MRZ décorative (bande machine-readable) */}
        <div
          className="absolute bottom-0 left-0 right-0 h-4 bg-black/60 flex items-center px-2 z-10"
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: '6px',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.05em',
          }}
        >
          <span className="truncate">
            D&lt;&lt;FRA{(nom || 'XXXX').toUpperCase().slice(0, 4)}&lt;{(prenom || 'X').toUpperCase().slice(0, 3)}&lt;&lt;&lt;&lt;{numero.replace(/\s/g, '')}&lt;&lt;C2R{yy}&lt;&lt;
          </span>
        </div>

        {/* Filigrane */}
        <div className="absolute bottom-4 right-2 text-[6px] font-black text-yellow-400/50 uppercase tracking-widest z-10">
          Cérémonie · Code 2 Roues
        </div>
      </div>

      {/* Mention non-officielle */}
      {showMention && (
        <div className="relative z-10 max-w-lg text-center px-4">
          <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/30 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-yellow-400 mb-1">
              ⚠ Cérémonie · visuel uniquement
            </div>
            <p className="text-xs text-white/70 leading-relaxed">
              Cette carte est un support de cérémonie. L&apos;attestation officielle de suivi de la formation AM sera délivrée par ton auto-école et enregistrée sur le portail officiel du permis de conduire.
            </p>
          </div>
        </div>
      )}

      {/* Keyframes locaux */}
      <style>{`
        @keyframes carte-appear {
          0%   { opacity: 0; transform: perspective(1000px) rotateY(-20deg) rotateX(10deg) scale(0.85); }
          60%  { opacity: 1; transform: perspective(1000px) rotateY(6deg) rotateX(-2deg) scale(1.03); }
          100% { opacity: 1; transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1); }
        }
        @keyframes carte-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 0.9; }
          100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default CartePermisAM;
